package com.havn.app.core.export

data class HavnXlsxSheet(
    val name: String,
    val rows: List<List<Any?>>
)

const val HavnXlsxMimeType: String = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

fun buildHavnXlsx(sheets: List<HavnXlsxSheet>): ByteArray {
    val normalizedSheets = sheets
        .filter { it.rows.isNotEmpty() }
        .ifEmpty { listOf(HavnXlsxSheet("Sheet1", listOf(listOf("HAVN")))) }
        .mapIndexed { index, sheet ->
            sheet.copy(name = sheet.name.xlsxSheetName().ifBlank { "Sheet${index + 1}" })
        }

    val files = buildList {
        add("[Content_Types].xml" to xlsxContentTypes(normalizedSheets.size).encodeToByteArray())
        add("_rels/.rels" to xlsxRootRels().encodeToByteArray())
        add("xl/workbook.xml" to xlsxWorkbook(normalizedSheets).encodeToByteArray())
        add("xl/_rels/workbook.xml.rels" to xlsxWorkbookRels(normalizedSheets.size).encodeToByteArray())
        normalizedSheets.forEachIndexed { index, sheet ->
            add("xl/worksheets/sheet${index + 1}.xml" to xlsxWorksheet(sheet.rows).encodeToByteArray())
        }
    }
    return zipStore(files)
}

private fun xlsxContentTypes(sheetCount: Int): String = buildString {
    append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
    append("""<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">""")
    append("""<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>""")
    append("""<Default Extension="xml" ContentType="application/xml"/>""")
    append("""<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>""")
    repeat(sheetCount) { index ->
        append("""<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>""")
    }
    append("</Types>")
}

private fun xlsxRootRels(): String =
    """<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>"""

private fun xlsxWorkbook(sheets: List<HavnXlsxSheet>): String = buildString {
    append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
    append("""<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>""")
    sheets.forEachIndexed { index, sheet ->
        append("""<sheet name="${sheet.name.xmlEscaped()}" sheetId="${index + 1}" r:id="rId${index + 1}"/>""")
    }
    append("</sheets></workbook>")
}

private fun xlsxWorkbookRels(sheetCount: Int): String = buildString {
    append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
    append("""<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">""")
    repeat(sheetCount) { index ->
        append("""<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>""")
    }
    append("</Relationships>")
}

private fun xlsxWorksheet(rows: List<List<Any?>>): String = buildString {
    append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
    append("""<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>""")
    rows.forEachIndexed { rowIndex, row ->
        val rowNumber = rowIndex + 1
        append("""<row r="$rowNumber">""")
        row.forEachIndexed { columnIndex, value ->
            append(xlsxCell(columnIndex, rowNumber, value))
        }
        append("</row>")
    }
    append("</sheetData></worksheet>")
}

private fun xlsxCell(columnIndex: Int, rowNumber: Int, value: Any?): String {
    val ref = "${xlsxColumnName(columnIndex)}$rowNumber"
    return when (value) {
        null -> """<c r="$ref"/>"""
        is Number -> """<c r="$ref"><v>${value.toString().xmlEscaped()}</v></c>"""
        is Boolean -> """<c r="$ref" t="b"><v>${if (value) 1 else 0}</v></c>"""
        else -> """<c r="$ref" t="inlineStr"><is><t>${value.toString().xmlEscaped()}</t></is></c>"""
    }
}

private fun xlsxColumnName(index: Int): String {
    var value = index + 1
    val chars = mutableListOf<Char>()
    while (value > 0) {
        val remainder = (value - 1) % 26
        chars += ('A'.code + remainder).toChar()
        value = (value - 1) / 26
    }
    return chars.asReversed().joinToString("")
}

private fun String.xlsxSheetName(): String =
    replace(Regex("""[\[\]\*:/\\?]"""), " ")
        .trim()
        .take(31)

private fun String.xmlEscaped(): String =
    replace("&", "&amp;")
        .replace("<", "&lt;")
        .replace(">", "&gt;")
        .replace("\"", "&quot;")
        .replace("'", "&apos;")

private data class ZipEntryData(
    val name: String,
    val bytes: ByteArray,
    val crc32: Long,
    val offset: Int
)

private fun zipStore(files: List<Pair<String, ByteArray>>): ByteArray {
    val out = ZipBytes()
    val entries = mutableListOf<ZipEntryData>()
    files.forEach { (name, bytes) ->
        val nameBytes = name.encodeToByteArray()
        val crc = crc32(bytes)
        val offset = out.size
        out.u32(0x04034b50)
        out.u16(20)
        out.u16(0)
        out.u16(0)
        out.u16(0)
        out.u16(0)
        out.u32(crc)
        out.u32(bytes.size.toLong())
        out.u32(bytes.size.toLong())
        out.u16(nameBytes.size)
        out.u16(0)
        out.bytes(nameBytes)
        out.bytes(bytes)
        entries += ZipEntryData(name, bytes, crc, offset)
    }

    val centralDirectoryOffset = out.size
    entries.forEach { entry ->
        val nameBytes = entry.name.encodeToByteArray()
        out.u32(0x02014b50)
        out.u16(20)
        out.u16(20)
        out.u16(0)
        out.u16(0)
        out.u16(0)
        out.u16(0)
        out.u32(entry.crc32)
        out.u32(entry.bytes.size.toLong())
        out.u32(entry.bytes.size.toLong())
        out.u16(nameBytes.size)
        out.u16(0)
        out.u16(0)
        out.u16(0)
        out.u16(0)
        out.u32(0)
        out.u32(entry.offset.toLong())
        out.bytes(nameBytes)
    }
    val centralDirectorySize = out.size - centralDirectoryOffset
    out.u32(0x06054b50)
    out.u16(0)
    out.u16(0)
    out.u16(entries.size)
    out.u16(entries.size)
    out.u32(centralDirectorySize.toLong())
    out.u32(centralDirectoryOffset.toLong())
    out.u16(0)
    return out.toByteArray()
}

private class ZipBytes {
    private val data = mutableListOf<Byte>()
    val size: Int get() = data.size

    fun u16(value: Int) {
        data += (value and 0xff).toByte()
        data += ((value ushr 8) and 0xff).toByte()
    }

    fun u32(value: Long) {
        data += (value and 0xff).toByte()
        data += ((value ushr 8) and 0xff).toByte()
        data += ((value ushr 16) and 0xff).toByte()
        data += ((value ushr 24) and 0xff).toByte()
    }

    fun bytes(bytes: ByteArray) {
        bytes.forEach { data += it }
    }

    fun toByteArray(): ByteArray = ByteArray(data.size) { index -> data[index] }
}

private fun crc32(bytes: ByteArray): Long {
    var crc = -1
    bytes.forEach { byte ->
        var current = (crc xor (byte.toInt() and 0xff)) and 0xff
        repeat(8) {
            current = if ((current and 1) != 0) (current ushr 1) xor 0xedb88320.toInt() else current ushr 1
        }
        crc = (crc ushr 8) xor current
    }
    return (crc xor -1).toLong() and 0xffffffffL
}
